import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import Modal, { ModalFooter, ModalProps } from '../../components/Modal'
import { errorToast, formatCurrency, successToast } from '../../util/Util'
import { PrimaryButton } from '../../components/Button'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import Radom, { Payout } from '../../state/Radom'
import { RADOM_COLORS } from '../../util/Constants'
import LoadingBar from '../../components/LoadingBar'
import { StatusBadge } from '../CheckoutSessions'
import { camelCase } from 'lodash'
import { ExternalLinkText } from '../Events'
import { formatExplorerUrl } from '../../util/Chains'
import LinkExternal from '../../components/LinkExternal'
import CopyButton from '../../components/CopyButton'

interface CreatePayoutProps extends ModalProps {
  payoutId: string
  onPayoutConfirmed: () => void
}

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-size: 14px;
`

const SummaryPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #edf1f7;
  border-radius: 16px;
  background: #fcfcfd;
  padding: 14px 16px;
`

const SummaryLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7d8492;
`

const SummaryValue = styled.span`
  color: #20242d;
  font-size: 15px;
  font-weight: 500;
`

const SectionTitle = styled.div`
  color: #20242d;
  font-size: 15px;
  font-weight: 500;
`

const GridHeader = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  gap: 14px;
  padding: 0 4px;

  > div {
    font-size: 11px;
    color: #7d8492;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`

const DataPanel = styled.div`
  border: 1px solid #edf1f7;
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
`

const DataList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 50vh;
  overflow: auto;
`

const DataRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-bottom: 1px solid #edf1f7;

  &:last-child {
    border-bottom: 0;
  }
`

const PayoutModal = observer(({ onPayoutConfirmed, payoutId, ...props }: CreatePayoutProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [payout, setPayout] = useState<Payout>()

  const getPayout = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const payout = await Radom.getPayout(payoutId)
      setPayout(payout)
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const confirmPayout = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await Radom.confirmPayout(payoutId)
      successToast('Successfully submitted payout')
      onPayoutConfirmed()
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const quotePayout = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await Radom.quotePayout(payoutId)
      successToast('Successfully reactivated payout')
      onPayoutConfirmed()
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getPayout()
  }, [])

  return <Modal title='Payout' {...props} innerStyles={{ width: 760 }}>
    <ContentStack>

      {
        payout &&
        <SummaryPanel>
          <SummaryCard>
            <SummaryLabel>Status</SummaryLabel>
            <StatusBadge status={camelCase(payout.status) as 'awaitingConfirmation' | 'pending' | 'complete' | 'expired' | 'processing' | 'failed'}>
              {payout.status}
            </StatusBadge>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>Total payout</SummaryLabel>
            <SummaryValue>{formatCurrency(payout?.totalAmountUsd, 'USD')}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>Gas fee</SummaryLabel>
            <SummaryValue>{formatCurrency(payout?.estimatedFeeUsd, 'USD')}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>Deduct fees</SummaryLabel>
            <SummaryValue>{payout.deductFees ? 'Yes' : 'No'}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>Email receipts</SummaryLabel>
            <SummaryValue>{payout.sendEmailReceipts ? 'Yes' : 'No'}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>Payout ID</SummaryLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SummaryValue style={{ fontSize: 14, wordBreak: 'break-all' }}>{payout.id}</SummaryValue>
              <CopyButton text={payout.id} />
            </div>
          </SummaryCard>
        </SummaryPanel>
      }

      {/* Crypto entries */}
      {payout && payout.cryptoEntries && payout.cryptoEntries.length > 0 && <>
        <SectionTitle>Crypto entries</SectionTitle>
        <GridHeader $columns='minmax(150px, 1.2fr) 120px minmax(220px, 1.4fr) minmax(110px, auto)'>
          <div>Asset</div>
          <div>Amount</div>
          <div>Payout address</div>
          <div>Explorer</div>
        </GridHeader>

        <DataPanel>
          <DataList>
            {
              isLoading && !payout && Array(10).fill(0).map((_, idx) => <React.Fragment key={idx}>
                <LoadingBar style={{ height: 30, width: '100%' }} />
                <LoadingBar style={{ height: 30, width: '100%' }} />
                <LoadingBar style={{ height: 30, width: '100%' }} />
                <LoadingBar style={{ height: 30, width: '100%' }} />
              </React.Fragment>
              )
            }
            {
              payout.cryptoEntries.map((entry, i) => (
                <DataRow key={entry.id} $columns='minmax(150px, 1.2fr) 120px minmax(220px, 1.4fr) minmax(110px, auto)'>
                  <div key={`${i}-m`}><PaymentMethodDisplay method={{ network: entry.network, token: entry.token }} /></div>
                  <div key={`${i}-a`} style={{ color: '#20242d', fontWeight: 500 }}>{entry.amount}</div>
                  <div key={`${i}-aa`} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0
                  }}>
                    <span style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      color: '#20242d',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12.5
                    }}>
                      {entry.payoutAddress}
                    </span>
                    <CopyButton text={entry.payoutAddress} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {
                      entry.txHash &&
                      <>
                        <CopyButton text={entry.txHash} />
                        <ExternalLinkText
                          target="_blank" rel="noreferrer"
                          href={formatExplorerUrl(
                            entry.network,
                            'tx',
                            entry.txHash
                          )}>
                          View
                          <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
                        </ExternalLinkText>
                      </>
                    }
                  </div>
                </DataRow>
              ))
            }
          </DataList>
        </DataPanel>
      </>}

      {/* Fiat entries */}
      {payout && payout.fiatEntries && payout.fiatEntries.length > 0 && <>
        <SectionTitle>Fiat entries</SectionTitle>
        <GridHeader $columns='minmax(220px, 1fr) 120px 110px'>
          <div>Beneficiary</div>
          <div>Amount</div>
          <div>Status</div>
        </GridHeader>

        <DataPanel>
          <DataList>
            {
              payout.fiatEntries.map((entry) => (
                <DataRow key={entry.id} $columns='minmax(220px, 1fr) 120px 110px'>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{ color: '#20242d', fontWeight: 500 }}>{entry.beneficiaryName || '-'}</span>
                    <span style={{ fontSize: 12, color: '#747894' }}>{entry.currency}</span>
                  </div>
                  <div style={{ color: '#20242d', fontWeight: 500 }}>{entry.amount} {entry.currency}</div>
                  <div style={{ color: '#5f6675' }}>{entry.status}</div>
                </DataRow>
              ))
            }
          </DataList>
        </DataPanel>
      </>}
    </ContentStack>

    <ModalFooter $fullWidthAction style={{ flexDirection: 'column', gap: 15 }}>

      {
        payout?.status === 'AwaitingConfirmation' && <>

          <PrimaryButton
            isLoading={isLoading}
            style={{ width: '100%' }}
            onClick={() => {
              confirmPayout()
            }}>
            Confirm payout
          </PrimaryButton>

        </>
      }

      {
        payout?.status === 'Expired' && <>

          <PrimaryButton
            isLoading={isLoading}
            style={{ width: '100%' }}
            onClick={() => {
              quotePayout()
            }}>
            Reactivate payout
          </PrimaryButton>
        </>
      }
    </ModalFooter>

  </Modal>
})

export default PayoutModal
